import React, { ReactNode } from 'react';
import {
    Autocomplete, AutocompleteValue, Chip, CircularProgress, MenuItem, TextField,
} from '@mui/material';

import useFetchOptions from './useFetchOptions';
import { isValidFunction } from '../../../utils/nullChecks';
import { getStringFromObject } from '../../../utils/lodash';
import { AutoComplete, AutoSuggestFieldProps } from '../types';

const AutoCompleteField =
    <T extends {},
        Multiple extends boolean,
        DisableClearable extends boolean,
        FreeSolo extends boolean>
    (props: AutoSuggestFieldProps<T, Multiple, DisableClearable, FreeSolo>) => {
        const {
            dataSourceApi,
            onChange,
            dataSourceConfig,
            label,
            getCustomLabel,
            getCustomLabelForListItem,
            removePaddingForListItems,
            multiple,
            fetchOnlyOnMount,
            multiline,
            required,
            value,
            error,
            helperText,
            inputRef,
            ...otherProps
        } = props;

        const [searchString, setSearchString] = React.useState<string>('');
        const searchText = fetchOnlyOnMount ? '' : searchString;
        const {
            options,
            isLoading,
        } = useFetchOptions<T>(dataSourceApi, searchText);

        const handleOptionChange = (
            event: React.SyntheticEvent,
            newValue: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
        ) => {
            if (isValidFunction(onChange)) {
                onChange(newValue as AutoComplete<T>, event);
            }
        };

        const handleTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchString(event.target.value);
        };

        const getOptionLabel = (option: T) => {
            if (isValidFunction(getCustomLabel)) {
                return getCustomLabel(option);
            }
            return getStringFromObject(dataSourceConfig!.value, option);
        };

        const getOptionLabelForListItem = (option: T) => {
            if (isValidFunction(getCustomLabelForListItem)) {
                return getCustomLabelForListItem(option);
            }
            return getOptionLabel(option);
        };

        const getOptionKey =
        (option: T) => getStringFromObject(dataSourceConfig!.key, option);

        const isOptionEqualToValue = (optionA: T, optionB: T) => (
            getOptionKey(optionA) === getOptionKey(optionB)
        );

        return (
            <Autocomplete
                {...otherProps}
                clearOnBlur
                blurOnSelect
                options={options}
                size="small"
                multiple={multiple}
                onChange={handleOptionChange}
                loading={isLoading}
                value={value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>}
                getOptionLabel={
                    (option) => getOptionLabel(option as T) as string
                }
                isOptionEqualToValue={isOptionEqualToValue}
                renderInput={
                    (textFieldProps) => (
                        <TextField
                            key={dataSourceConfig!.key}
                            label={label}
                            test-id={label !== '' ? label : 'search-header'}
                            value={searchString}
                            onChange={handleTextInputChange}
                            multiline={multiline}
                            required={required}
                            error={error}
                            helperText={helperText}
                            inputRef={inputRef}
                            {...textFieldProps}
                            InputProps={{
                                ...textFieldProps.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {textFieldProps.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )
                }
                renderOption={
                    (optionProps, option) => (
                        <MenuItem
                            key={getOptionKey(option) as string}
                            {...optionProps}
                            divider
                            sx={{ padding: removePaddingForListItems ? '0px!important ' : '10px' }}
                        >
                            {getOptionLabelForListItem(option) as ReactNode}
                        </MenuItem>
                    )
                }
                renderTags={
                    multiple ? (values, getTagProps) => (
                        values.map((option, index: number) => (
                            <Chip
                                variant="outlined"
                                size="small"
                                label={getOptionLabel(option) as string}
                                {...getTagProps({ index })}
                                key={getOptionKey(option) as string}
                            />
                        ))) : undefined
                }
            />
        );
    };

AutoCompleteField.defaultProps = {
    getCustomLabel: null,
    dataSourceConfig: {
        key: 'key',
        value: 'value',
    },
    label: '',
    multiple: false,
    fetchOnlyOnMount: false,
    value: null,
    multiline: false,
    required: false,
    removePaddingForListItems: false,
};

export default AutoCompleteField;
